import React, { useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { INicheMap, IGif } from "services/types";
import { IUserList } from "./GifList";
import { Player } from "components/player/Player";
import { useFullScreen } from "hooks/useFullScreen";
import { useFSNav } from "hooks/useFSNav";
import { ViewRegion } from "utils/metrics";
import { PlayerPlaceholder } from "components/UI/Skeleton";
import { deviceTypeEnum, useDeviceType } from "hooks/useDeviceType";
import { PopupManager } from "components/popups/PopupManager";

import "./PreviewFeed.scss";

interface IProps {
  gifs: IGif[];
  users: IUserList;
  niches?: INicheMap;
  hasMore: boolean;
  fetchMore: () => void;
  injector?: (pos: number, gif: IGif, fullScreen: boolean) => JSX.Element;
  rel?: string;
  source: ViewRegion;
  sourceId: string | null;
  isLoading?: boolean;
}

const PLACEHOLDER_ITEMS_LENGTH = 6;

export const PreviewFeed = (props: IProps) => {
  const [hiddenGifs, setHiddenGifs] = useState<string[]>([]);
  const filteredGifs = props.gifs.filter((gif) => !hiddenGifs.includes(gif.id));
  const feed = useRef<HTMLDivElement>(null);
  const deviceType = useDeviceType();
  const isDesktop = deviceType === deviceTypeEnum.DESKTOP;
  const {
    inFullscreenMode,
    toggleFullscreen,
    fullscreenElement,
    FSGif
  } = useFullScreen({ ref: feed });

  useFSNav({
    isFS: inFullscreenMode,
    gifs: props.gifs,
    fetchMore: props.fetchMore,
    hasMore: props.hasMore,
    ref: feed,
    toggleFullscreen,
    FSGif
  });

  const hideGif = (id: string) => {
    setHiddenGifs((prev: string[]) => [...prev, id]);
  };

  const renderGifs = () => {
    if (!filteredGifs.length && props.isLoading) {
      return new Array(PLACEHOLDER_ITEMS_LENGTH).fill("").map((___, index) => (
        <React.Fragment key={index}>
          <PlayerPlaceholder />
          {/* @ts-ignore */}
          {props.injector && props.injector(index, { id: "load" }, inFullscreenMode)}
        </React.Fragment>
      ));
    }

    if (!filteredGifs.length && !props.isLoading) {
      return <p>No gifs to show on this page.</p>;
    }

    return filteredGifs.map((gif: IGif, index: number) => (
      <React.Fragment key={index}>
        <Player
          key={gif.id}
          gif={gif}
          user={props.users[gif.userName]}
          niches={props.niches}
          fullscreen={inFullscreenMode}
          toggleFullScreen={toggleFullscreen}
          rel={props.rel}
          position={index + 1}
          source={props.source}
          sourceId={props.sourceId}
          hideGif={hideGif}
        />

        {props.injector && props.injector(index, gif, inFullscreenMode)}
      </React.Fragment>
    ));
  };

  return (
    <>
      <InfiniteScroll
        dataLength={props.gifs.length}
        // todo: implement fetch more for FS.
        hasMore={props.hasMore}
        next={props.fetchMore}
        loader={<></>}
        scrollableTarget={!isDesktop ? "scrollableDiv" : undefined}
      >
        <div className="previewFeed" ref={feed}>
          {renderGifs()}
          {fullscreenElement && <PopupManager />}
        </div>
      </InfiniteScroll>
    </>
  );
};

export default PreviewFeed;
